import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  TruckIcon,
  PlayIcon,
  EyeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { getRoutes, type TransportRoute } from '@/api/routesApi'
import toast from 'react-hot-toast'

const DriverRoutesPage: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [routes, setRoutes] = useState<TransportRoute[]>([])
  const [selectedRoute, setSelectedRoute] = useState<TransportRoute | null>(null)

  useEffect(() => {
    fetchRoutes()
  }, [])

  const fetchRoutes = async () => {
    setLoading(true)
    try {
      const response = await getRoutes({ assignedDriverId: user?.id })
      setRoutes(response.data?.routes || [])
    } catch (error) {
      console.error('Failed to fetch routes:', error)
      toast.error('Failed to load routes')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'suspended':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-blue-100 text-blue-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <MapPinIcon className="h-8 w-8 text-blue-600 mr-3" />
            My Routes
          </h1>
          <p className="text-gray-600 mt-1">
            View and manage your assigned transport routes
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Total Routes</p>
          <p className="text-2xl font-bold text-blue-600">{routes.length}</p>
        </div>
      </div>

      {/* Routes List */}
      {routes.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center"
        >
          <MapPinIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Routes Assigned</h3>
          <p className="text-gray-500 mb-4">
            You don't have any routes assigned yet. Please contact your administrator.
          </p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {routes.map((route, index) => (
            <motion.div
              key={route.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Route Header */}
              <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{route.routeName || route.routeNumber}</h3>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full bg-white/20 text-white`}>
                    {route.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-blue-100 text-sm mt-1">{route.description || 'Transport route'}</p>
              </div>

              {/* Route Stats */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <MapPinIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{route.stops?.length || 0}</p>
                    <p className="text-xs text-gray-500">Stops</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <UserGroupIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{route.stopCount || route.stops?.length || 0}</p>
                    <p className="text-xs text-gray-500">Students</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <ClockIcon className="h-5 w-5 text-orange-600" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{route.operatingDays?.length || 0} days</p>
                    <p className="text-xs text-gray-500">Per Week</p>
                  </div>
                </div>

                {/* Route Times */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center">
                      <CalendarDaysIcon className="h-4 w-4 mr-1" />
                      Departure
                    </span>
                    <span className="font-medium text-gray-900">{route.schedule?.departure?.time || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center">
                      <CalendarDaysIcon className="h-4 w-4 mr-1" />
                      Arrival
                    </span>
                    <span className="font-medium text-gray-900">{route.schedule?.arrival?.time || 'N/A'}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedRoute(route)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View Details
                  </button>
                  <button className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                    <PlayIcon className="h-4 w-4 mr-1" />
                    Start Trip
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Route Details Modal */}
      {selectedRoute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedRoute.routeName || selectedRoute.routeNumber}</h2>
                  <p className="text-gray-500">{selectedRoute.description || 'Route details'}</p>
                </div>
                <button
                  onClick={() => setSelectedRoute(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Route Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Total Stops</p>
                  <p className="text-xl font-semibold text-blue-900">{selectedRoute.stops?.length || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600">Students</p>
                  <p className="text-xl font-semibold text-purple-900">{selectedRoute.stopCount || selectedRoute.stops?.length || 0}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-600">Operating Days</p>
                  <p className="text-xl font-semibold text-orange-900">{selectedRoute.operatingDays?.length || 0}/week</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Status</p>
                  <p className="text-xl font-semibold text-green-900">{selectedRoute.isActive ? 'Active' : 'Inactive'}</p>
                </div>
              </div>

              {/* Stops List */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Route Stops</h3>
                {selectedRoute.stops && selectedRoute.stops.length > 0 ? (
                  <div className="space-y-2">
                    {selectedRoute.stops.map((stop: any, index: number) => (
                      <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{stop.name || `Stop ${index + 1}`}</p>
                          <p className="text-sm text-gray-500">{stop.address || 'No address'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{stop.time || '--:--'}</p>
                          <p className="text-xs text-gray-500">{stop.students || 0} students</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No stops defined for this route</p>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedRoute(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                <PlayIcon className="h-4 w-4 mr-2" />
                Start Trip
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default DriverRoutesPage
